/* readiness — permit and materials as STATES, not booleans.

   The old model had one checkbox each, and both could be set by something that
   isn't the thing they claim:

     · attachPermit() set `permit: true` from a file picker, so choosing a file
       approved the permit. It also kept only the file NAME — the document
       itself was never stored, so nobody could reopen it.
     · "Materials in" fired on an ordered flag plus an estimated delivery date.
       An ETA is a promise from the supplier, not a receipt.

   A permit can also be legitimately not required, which a checkbox can only
   express by lying in one direction or the other.

   Materials derive from the job's purchase orders when it has any — that is
   already the record of what was ordered and what has been received — and fall
   back to the manual flag when the contractor never raised a PO. */

export type PermitState = "not-required" | "needed" | "submitted" | "approved";

export const PERMIT_STATES: { id: PermitState; label: string; sub: string; done: boolean }[] = [
  { id: "needed", label: "Needed", sub: "Nothing filed yet", done: false },
  { id: "submitted", label: "Submitted", sub: "Filed, waiting on the jurisdiction", done: false },
  { id: "approved", label: "Approved", sub: "Issued — clear to dig", done: true },
  { id: "not-required", label: "Not required", sub: "No permit needed for this job", done: true },
];

export function permitState(wo: any): PermitState {
  const r = (wo && wo.readiness) || {};
  const pi = r.permitInfo || {};
  if (r.permitNotRequired) return "not-required";
  if (r.permit || pi.approvedAt) return "approved";
  if (pi.submittedAt) return "submitted";
  return "needed";
}

/** the readiness patch for moving the permit to `to`. Approval is a decision a
    person makes here; it is never a side effect of attaching a file. */
export function permitPatch(r: any, to: PermitState): any {
  const pi = { ...((r && r.permitInfo) || {}) };
  if (to === "approved") {
    if (!pi.approvedAt) pi.approvedAt = Date.now();
    if (!pi.submittedAt) pi.submittedAt = pi.approvedAt;
    return { permit: true, permitNotRequired: false, permitInfo: pi };
  }
  if (to === "submitted") {
    if (!pi.submittedAt) pi.submittedAt = Date.now();
    delete pi.approvedAt;
    return { permit: false, permitNotRequired: false, permitInfo: pi };
  }
  if (to === "not-required") {
    return { permit: false, permitNotRequired: true, permitInfo: pi };
  }
  delete pi.submittedAt; delete pi.approvedAt;
  return { permit: false, permitNotRequired: false, permitInfo: pi };
}

/** does the permit leg of site readiness count as settled? */
export function permitDone(wo: any) {
  const st = permitState(wo);
  return st === "approved" || st === "not-required";
}

export type MaterialsState = "not-ordered" | "ordered" | "partial" | "received";

export const MATERIALS_LABEL: Record<MaterialsState, string> = {
  "not-ordered": "Not ordered",
  ordered: "Ordered",
  partial: "Partially received",
  received: "Received",
};

/* A job's materials position. `pos` is how many purchase orders it is derived
   from — zero means this is the contractor's own flag, which can only say
   ordered or not. */
export function materialsState(wo: any, pos: any[], derive: (po: any) => string): { state: MaterialsState; label: string; detail: string; pos: number } {
  const live = (pos || []).filter((p: any) => {
    const st = derive(p);
    return st !== "canceled" && st !== "draft";
  });
  if (live.length) {
    const sts = live.map(derive);
    const state: MaterialsState = sts.every((s) => s === "received") ? "received"
      : sts.some((s) => s === "received" || s === "partial") ? "partial"
      : "ordered";
    const recv = sts.filter((s) => s === "received").length;
    return {
      state, label: MATERIALS_LABEL[state], pos: live.length,
      detail: state === "received" ? `all ${live.length} purchase order${live.length === 1 ? "" : "s"} received`
        : state === "partial" ? `${recv} of ${live.length} purchase orders received`
        : `${live.length} purchase order${live.length === 1 ? "" : "s"} out`,
    };
  }
  const ordered = !!(wo && wo.suppliesOrdered);
  const received = !!(wo && wo.materialsReceived);
  const state: MaterialsState = received ? "received" : ordered ? "ordered" : "not-ordered";
  return {
    state, label: MATERIALS_LABEL[state], pos: 0,
    detail: received ? "marked received on the job"
      : ordered ? (wo && wo.materialEta ? "supplier ETA only — not a receipt" : "no delivery date yet")
      : "nothing ordered yet",
  };
}
